/**
 * Modal for generating a self-service license link
 */
import React, { useState } from 'react';
import { Modal, Form, Input, Button, Typography, Space, message } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { selfServiceApi } from '../api';

const { Text } = Typography;

interface GenerateLinkModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreated: () => void;
}

const GenerateLinkModal: React.FC<GenerateLinkModalProps> = ({
  visible,
  onCancel,
  onCreated,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const link = await selfServiceApi.create({ notes: values.notes || '' });
      const url = link.link_url.startsWith('http')
        ? link.link_url
        : `${window.location.origin}${link.link_url}`;
      setGeneratedUrl(url);
      onCreated();
    } catch (error: any) {
      message.error(error.message || 'Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      message.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setGeneratedUrl(null);
    setCopied(false);
    onCancel();
  };

  return (
    <Modal
      title="Generate Self-Service License Link"
      open={visible}
      onCancel={handleClose}
      footer={
        generatedUrl
          ? [
              <Button key="close" onClick={handleClose}>
                Close
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
              <Button key="create" type="primary" loading={loading} onClick={handleCreate}>
                Generate Link
              </Button>,
            ]
      }
    >
      {!generatedUrl ? (
        <Form form={form} layout="vertical">
          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea
              rows={3}
              placeholder="Internal notes — e.g. customer name, ticket number"
            />
          </Form.Item>
        </Form>
      ) : (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Share this single-use link with the customer. It will expire after one use.
          </Text>
          <Space.Compact style={{ width: '100%' }}>
            <Input value={generatedUrl} readOnly style={{ fontFamily: 'monospace' }} />
            <Button
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={handleCopy}
              type={copied ? 'default' : 'primary'}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Space.Compact>
        </div>
      )}
    </Modal>
  );
};

export default GenerateLinkModal;
